/* eslint-disable */
import { Button, Checkbox, Dropdown, Flex, Form, Modal, Popover, Space, Table, Tag, message } from "antd";
import React, { useMemo, useRef, useState } from "react";
import {
  CaretDownOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  MenuOutlined,
  OneToOneOutlined,
  PlusOutlined,
  SyncOutlined,
  UserSwitchOutlined,
  UsergroupAddOutlined,
} from "@ant-design/icons";
import moment from "moment";
import http from "@/utils/http";
import api from "@/utils/api";
import commonFn from "@/utils/commonFn";
import RoleMenuModal from "./component/RoleMenuModal";
import UpdateModal from "./component/UpdateModal";
import AddModal from "./component/AddModal";

export default function UserList() {
  const updateModalRef = useRef();
  const addModalRef = useRef();
  const roleMenuModalRef = useRef();

  const [tableData, setTableData] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 100, total: 0 });
  const [visibleKeys, setVisibleKeys] = useState([]); // 当前显示的列
  const [tableKey, setTableKey] = useState(0); // 改变 key 可让表头输入框重置

  // 用 ref 保存最新的分页/筛选条件，避免闭包拿到旧值
  const paginationRef = useRef({ current: 1, pageSize: 100 });
  const filterRef = useRef({ roleName: "" });

  // ================= 请求列表 =================
  const fetchData = async (
    page = 1,
    pageSize = paginationRef.current.pageSize,
    filter = filterRef.current
  ) => {
    setTableLoading(true);
    try {
      const response = await http.post(api.getRolePage, {
        page,
        count: pageSize,
        roleName: filter?.roleName || "",
      });
      const list = (response?.data?.list || []).map((item) => ({
        ...item,
        key: item.roleId,
      }));
      setTableData(list);
      paginationRef.current = { current: page, pageSize };
      setPagination({ current: page, pageSize, total: response?.data?.total || 0 });
    } catch (err) {
      console.error("请求失败:", err);
      message.error("Failed to load data.");
    } finally {
      setTableLoading(false);
    }
  };

  // 刷新当前页(保持当前筛选)
  const reload = () => fetchData(paginationRef.current.current, paginationRef.current.pageSize);

  // ================= 刷新按钮:清空筛选 + 回到第一页 =================
  const handleRefresh = () => {
    filterRef.current = { roleName: "" };
    setTableKey((k) => k + 1);
    fetchData(1, paginationRef.current.pageSize, filterRef.current);
  };

  // ================= 表头筛选(回车) =================
  const handlePressRoleName = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      filterRef.current = { ...filterRef.current, roleName: e.target.value };
      fetchData(1, paginationRef.current.pageSize);
    }
  };

  // ================= Action 菜单 =================
  const handleMenuClick = (e, record) => {
    if (e.key === "reset") {
      updateModalRef.current?.showModal(record);
    }
    if (e.key === "menu") {
      roleMenuModalRef.current?.showModal(record);
    }
    if (e.key === "role") {
      const paramString = new URLSearchParams(record).toString();
      const url = `${window.location.origin}#/UserManagment/RoleManagement${
        paramString ? `?${paramString}` : ""
      }`;
      window.open(url, "_blank", "noopener,noreferrer");
    }
    if (e.key === "delete") {
      Modal.confirm({
        title: "Complete Request",
        icon: <ExclamationCircleOutlined />,
        content: <span>Are you sure to delete this role?</span>,
        okText: "Sure",
        cancelText: "Cancel",
        style: { top: "40%" },
        async onOk() {
          try {
            const response = await http.get(api.getDelRole, {
              params: { roleId: record.roleId },
            });
            if (response && response.code == 1) {
              message.success("Successfully delete.");
              reload();
            } else {
              message.error(response?.msg);
            }
          } catch (error) {
            message.error("An error occurred. Please try again.");
          }
        },
      });
    }
  };

  // ================= 列定义(只创建一次,内部都通过 ref 取最新值) =================
  const allColumns = useMemo(() => {
    const iconStyle = { color: "#00c0ef", fontSize: "17px" };
    const initColumns = [
      {
        title: "Action",
        key: "action",
        dataIndex: "propId",
        align: "center",
        width: 60,
        render: (text, record) =>
          (commonFn.btnAuthorityFlag("AddUserAccount") ||
            commonFn.btnAuthorityFlag("RolePermission") ||
            commonFn.btnAuthorityFlag("Update") ||
            commonFn.btnAuthorityFlag("DeleteRole")) && (
            <Dropdown
              menu={{
                items: [
                  commonFn.btnAuthorityFlag("AddUserAccount") && {
                    key: "role",
                    icon: <UserSwitchOutlined style={iconStyle} />,
                    label: <div>Add User Account</div>,
                  },
                  commonFn.btnAuthorityFlag("RolePermission") && {
                    key: "menu",
                    icon: <UsergroupAddOutlined style={iconStyle} />,
                    label: <div>Role Permission</div>,
                  },
                  commonFn.btnAuthorityFlag("Update") && {
                    key: "reset",
                    icon: <EditOutlined style={iconStyle} />,
                    label: <div>Update</div>,
                  },
                  commonFn.btnAuthorityFlag("DeleteRole") && {
                    key: "delete",
                    icon: <DeleteOutlined style={iconStyle} />,
                    label: <div>Delete Role</div>,
                  },
                ].filter(Boolean),
                onClick: (e) => handleMenuClick(e, record),
              }}
              trigger={["click"]}
            >
              <MenuOutlined style={{ cursor: "pointer", fontSize: "16px", color: "#0e78a9" }} />
            </Dropdown>
          ),
      },
      {
        title: "Role",
        dataIndex: "roleName",
        key: "roleName",
        titleType: "Input",
        width: 300,
        align: "left",
        onChange: handlePressRoleName,
      },
      {
        title: "Role Remark",
        dataIndex: "remark",
        key: "remark",
        titleType: "",
        width: 300,
        align: "center",
      },
      {
        title: "Account Count",
        dataIndex: "accountCount",
        key: "accountCount",
        width: 120,
        align: "center",
      },
      {
        title: "Self-change Acct",
        dataIndex: "isAdmin",
        key: "isAdmin", // 原来缺少 key
        titleType: "",
        selectOptions: [
          { label: "No", value: 0 },
          { label: "Yes", value: 1 },
        ],
        render: (text, record) => {
          const { roleId } = record;
          const handleClick = () => {
            Modal.confirm({
              title: "Confirm Self-change Acct Change",
              content: (
                <span>
                  Are you sure you want to change the Self-change Acct to
                  <span style={{ color: "#1890ff", fontWeight: 500 }}>
                    {text ? " No" : " Yes"}
                  </span>
                  ?
                </span>
              ),
              okText: "Confirm",
              cancelText: "Cancel",
              style: { top: "40%" },
              onOk: async () => {
                try {
                  const response = await http.post(api.updateRoleAdmin, {
                    roleId,
                    isAdmin: text ? 0 : 1,
                  });
                  if (response?.code === "1") {
                    message.success("Self-change Acct updated successfully");
                    reload();
                  }
                } catch (err) {
                  message.error("An error occurred. Please try again.");
                }
              },
            });
          };
          return (
            <Tag color={text ? "green" : "red"} style={{ cursor: "pointer" }} onClick={handleClick}>
              {text ? "Yes" : "No"}
            </Tag>
          );
        },
      },
      {
        title: "Last Updated By",
        dataIndex: "updateName",
        key: "updateName",
        width: 300,
        align: "center",
      },
      {
        title: "Last Update Time",
        dataIndex: "updateTime",
        key: "updateTime",
        width: 120,
        align: "center",
        render: (text) => (text ? moment(text).format("MM/DD/YYYY") : " "),
      },
      {
        title: "Create Time",
        dataIndex: "createTime",
        key: "createTime",
        width: 120,
        align: "center",
        render: (text) => (text ? moment(text).format("MM/DD/YYYY") : " "),
      },
    ];
    const cols = commonFn.handleColunmFn(initColumns);
    // 初始化:全部列可见
    setVisibleKeys(cols.map((c) => c.key));
    return cols;
  }, []);

  // 列显示/隐藏
  const displayColumns = allColumns
    .map((col) => ({ ...col, hidden: !visibleKeys.includes(col.key) }))
    .filter((col) => !col.hidden);

  const columnOptions = allColumns.map((col) => ({ label: col.title, value: col.key }));

  // 首次加载
  React.useEffect(() => {
    fetchData(1, 100);
  }, []);

  // ================= 渲染 =================
  const columnSelector = (
    <Checkbox.Group
      options={columnOptions}
      value={visibleKeys}
      onChange={setVisibleKeys}
      style={{ display: "flex", flexDirection: "column", gap: 8 }}
    />
  );

  return (
    <div>
      <Flex className="topDiv" justify="space-between" align="flex-start">
        <div>
          {commonFn.btnAuthorityFlag("Add") && (
            <Space size="large">
              <Button className="btnNew" onClick={() => addModalRef.current?.setModelAdd()}>
                <PlusOutlined />
                New
              </Button>
            </Space>
          )}
        </div>
        <Space.Compact>
          <Button onClick={handleRefresh} loading={tableLoading} icon={<SyncOutlined />} />
          <Popover content={columnSelector} title="" trigger="click">
            <Button>
              <OneToOneOutlined />
              <CaretDownOutlined className="ft12" />
            </Button>
          </Popover>
        </Space.Compact>
      </Flex>

      <Table
        key={tableKey}
        loading={tableLoading}
        columns={displayColumns}
        dataSource={tableData}
        rowKey="roleId"
        pagination={{
          showQuickJumper: true,
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total) => `Total: ${total} items`,
        }}
        onChange={(p) => fetchData(p.current, p.pageSize)}
        scroll={{ x: "max-content", y: "calc(100vh - 290px)" }}
        className="my-Table"
      />

      <UpdateModal ref={updateModalRef} onSuccess={reload} />
      <AddModal ref={addModalRef} onSuccess={reload} />
      <RoleMenuModal ref={roleMenuModalRef} onSuccess={reload} />
    </div>
  );
}